'use client';

import { DisclosureAddOnProps, DisclosureProps, ModalProps } from '@/libs/contentful/props';
import { KeyLinePosition, Margins } from '@/types/constants';
import { cn, getSectionProps } from '@/utils/styles';
import Disclosure from '@/components/DisclosureGroupings/Disclosure';
import BaseModal from '@/components/shared/BaseModal/BaseModal';
import KeyLine from '@/components/shared/KeyLine';
import { useConsumedDisclosures, useDisclosureNumberingMap, useDisclosureContext } from '@/contexts/DisclosureProvider';
import { useEffect, useMemo } from 'react';

// Type guard to check if an item is a Disclosure
const isDisclosure = (item: DisclosureProps | ModalProps): item is DisclosureProps => {
  return (item as DisclosureProps).copy !== undefined && !('headline' in item && 'disclaimerText' in item);
};

// Type guard to check if an item is a Modal
const isModal = (item: DisclosureProps | ModalProps): item is ModalProps => {
  return 'headline' in item && 'disclaimerText' in item;
};

const getMarginStyles = (margins?: Margins, showTopKeyline?: boolean) => {
  // When keyline is shown, we need to adjust top padding
  if (showTopKeyline) {
    const marginStyles: { [key in Margins]: string } = {
      [Margins.NoTop]: 'pt-0 pb-6',
      [Margins.NoBottom]: 'pt-4 pb-0', // Add some space after keyline
      [Margins.NoMargins]: 'pt-4 pb-2',
      [Margins.Centered]: 'pt-4 pb-6', // Reduced top padding when keyline present
    };
    return margins ? marginStyles[margins] : marginStyles[Margins.NoMargins];
  }

  const marginStyles: { [key in Margins]: string } = {
    [Margins.NoTop]: 'pt-0 pb-6',
    [Margins.NoBottom]: 'pt-6 pb-0',
    [Margins.NoMargins]: 'py-2',
    [Margins.Centered]: 'py-6',
  };

  return margins ? marginStyles[margins] : marginStyles[Margins.NoMargins];
};

/**
 * DisclosureAddOn - A module that displays specific disclosures inline
 *
 * When disclosures are shown in this component, they are "consumed" and
 * will NOT be duplicated in the footer's DynamicDisclosures component.
 *
 * Auto-numbering is only shown when the disclosure is embedded in content above.
 * Symbols are always shown when the disclosure has one, even if only in this add-on.
 */
const DisclosureAddOn = ({
  sys,
  sectionName,
  disclosuresCollection,
  color,
  margins,
  keyLine,
}: DisclosureAddOnProps) => {
  const items = useMemo(() => disclosuresCollection?.items ?? [], [disclosuresCollection?.items]);
  const { consumeDisclosures } = useConsumedDisclosures();
  const disclosureNumberingMap = useDisclosureNumberingMap();
  const { allDisclosures } = useDisclosureContext();
  const showTopKeyline = keyLine === KeyLinePosition.Top;

  // Register disclosures (not modals) as "consumed" so they won't appear in DynamicDisclosures
  useEffect(() => {
    const disclosureItems = items.filter(isDisclosure);
    if (disclosureItems.length > 0) {
      const disclosureIds = disclosureItems
        .filter((d): d is DisclosureProps & { sys: { id: string } } => !!d.sys?.id)
        .map(d => d.sys.id);

      return consumeDisclosures(sys?.id ?? 'disclosure-addon', disclosureIds);
    }
  }, [items, consumeDisclosures, sys?.id]);

  if (!items.length) {
    return null;
  }

  // Check if a disclosure is embedded somewhere in the page content
  const isDisclosureEmbedded = (disclosureId: string | undefined): boolean => {
    if (!disclosureId) return false;
    return allDisclosures.some(d => d.sys?.id === disclosureId);
  };

  return (
    <section {...getSectionProps(color, undefined, sectionName)} data-testid="disclosureAddOn">
      <div className="content-wrapper">
        {showTopKeyline && <KeyLine />}
        <div className={cn('flex flex-col gap-y-2', getMarginStyles(margins, showTopKeyline))}>
          {items.map((item, index) => {
            // Handle Modal type
            if (isModal(item)) {
              return (
                <BaseModal
                  key={item.sys?.id || `modal-${index}`}
                  sys={item.sys}
                  title={item.headline}
                  body={item.copy}
                  openButtonText={item.disclaimerText}
                />
              );
            }

            // Handle Disclosure type
            if (isDisclosure(item)) {
              const disclosureId = item.sys?.id;
              const isEmbedded = isDisclosureEmbedded(disclosureId);

              // Only show auto-number if the disclosure is embedded in content above
              const autoNumber =
                isEmbedded && !item.symbol && disclosureId ? disclosureNumberingMap.get(disclosureId) : undefined;

              // Always show symbol when the disclosure has one (even if only in this add-on)
              const effectiveSymbol = item.symbol;

              return (
                <Disclosure key={disclosureId || index} {...item} symbol={effectiveSymbol} autoNumber={autoNumber} />
              );
            }

            return null;
          })}
        </div>
      </div>
    </section>
  );
};

export default DisclosureAddOn;
